import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import PosterFrame from "@/components/PosterFrame";
import { toast } from "sonner";

/* Poster-wall card: black canvas + lime marker on the headline-word.
   Uniform look across Home / Events / City pages. */
export default function EventCard({ event, index = 0, favorited = false, isPast = false, onToggleFav }) {
  const { user } = useAuth();
  const [isFav, setIsFav] = useState(favorited);
  const dateStr = event.date ? new Date(event.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }).toUpperCase() : "";

  // P2.2 multi-day — list of YYYY-MM-DD strings; empty for single-day
  const dayList = Array.isArray(event.day_dates) && event.day_dates.length ? event.day_dates : (event.date ? [event.date] : []);
  const isMulti = event.is_multi_day || dayList.length > 1;
  const parseDay = (iso) => {
    try {
      const d = new Date(iso);
      return { dd: d.toLocaleDateString("en-IN", { day: "2-digit" }), mmm: d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase() };
    } catch { return { dd: "--", mmm: "" }; }
  };
  const singleDay = parseDay(dayList[0] || event.date || "");

  // Themed accent — explicit event.accent_theme OR rotate across the feed so
  // the poster-wall looks like a kaleidoscopic magazine spread (PDF reference).
  const THEMES = ["pink", "yellow", "blue", "purple"];
  const accent = (event.accent_theme && THEMES.includes(event.accent_theme))
    ? event.accent_theme
    : THEMES[index % THEMES.length];
  const accentConfig = {
    pink:   { ring: "hover:border-pink/70",    dateBg: "bg-pink text-white",       marker: "marker-pink",   corner: "bg-pink" },
    yellow: { ring: "hover:border-[#FFC600]",  dateBg: "bg-[#FFC600] text-ink",    marker: "marker-yellow", corner: "bg-[#FFC600]" },
    blue:   { ring: "hover:border-skyx",       dateBg: "bg-skyx text-ink",         marker: "marker-blue",   corner: "bg-skyx" },
    purple: { ring: "hover:border-purple",     dateBg: "bg-purple text-white",     marker: "marker-purple", corner: "bg-purple" },
  }[accent];

  const toggleFav = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast("Log in to save events"); return; }
    try {
      const { data } = await api.post("/favorites/toggle", { event_id: event.id });
      setIsFav(data.favorited);
      onToggleFav?.(event.id, data.favorited);
      toast.success(data.favorited ? "Saved to your list" : "Removed");
    } catch { toast.error("Could not save"); }
  };

  const minPrice = (() => {
    const prices = (event.tiers || []).map((t) => Number(t.price || 0)).filter((p) => p > 0);
    return prices.length ? Math.min(...prices) : 0;
  })();

  // Pick the last word of the title to wrap in a lime marker — poster wall effect.
  const titleParts = (event.title || "").trim().split(/\s+/);
  const titleHead = titleParts.slice(0, -1).join(" ");
  const titleTail = titleParts.slice(-1)[0] || event.title;

  return (
    <div className={`group event-poster-card relative poster-canvas rounded-3xl overflow-hidden border border-white/10 ${accentConfig.ring} transition-colors`} data-accent={accent}>
      <Link to={`/events/${event.id}`} data-testid={`event-card-${event.id}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden">
          {event.banner || event.banner_portrait ? (
            <>
              <img src={event.banner_portrait || event.banner} alt={event.title} loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${isPast ? "grayscale opacity-50" : ""}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent pointer-events-none" />
            </>
          ) : (
            <PosterFrame event={event} className={isPast ? "grayscale opacity-60" : ""} />
          )}

          {/* P2.2 Big visible date — torn-calendar badge (single) or stacked ladder (multi) */}
          <div className={`absolute top-3 left-3 z-[1] ${isPast ? "opacity-50 grayscale" : ""}`}>
            {isMulti ? (
              <div className="flex flex-col gap-1" data-testid={`event-date-stack-${event.id}`}>
                {dayList.slice(0, 3).map((iso, i) => {
                  const d = parseDay(iso);
                  return (
                    <div key={iso + i} className={`${accentConfig.corner} rounded-md px-2 py-1 shadow-md -ml-${i*2}`}
                         style={{ marginLeft: i * -4, color: accent === "yellow" ? "#0A0A0F" : "#FFFFFF" }}>
                      <div className="font-fancy text-lg leading-none">{d.dd}</div>
                      <div className="font-mono text-[8px] uppercase tracking-widest leading-none mt-0.5">{d.mmm}</div>
                    </div>
                  );
                })}
                {dayList.length > 3 && (
                  <div className="rounded-md bg-black/70 text-white font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 ml-1">+{dayList.length - 3}</div>
                )}
              </div>
            ) : (
              <div className={`${accentConfig.corner} rounded-xl px-3 py-2 shadow-xl rotate-[-3deg]`}
                   style={{ color: accent === "yellow" ? "#0A0A0F" : "#FFFFFF" }}
                   data-testid={`event-date-badge-${event.id}`}>
                <div className="font-fancy text-3xl leading-none">{singleDay.dd}</div>
                <div className="font-mono text-[9px] uppercase tracking-widest leading-none mt-1">{singleDay.mmm}</div>
              </div>
            )}
          </div>

          {/* Top-right — city + multi-day chip */}
          <div className="absolute top-3 right-3 flex flex-wrap items-center gap-1.5 justify-end z-[1]">
            {isMulti && (
              <span className="inline-flex items-center gap-1 bg-lime text-ink px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest rounded-full shadow" data-testid={`event-multi-day-chip-${event.id}`}>
                {dayList.length}·DAY
              </span>
            )}
            <span className="inline-flex items-center gap-1 bg-black/60 backdrop-blur text-white border border-white/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest rounded-full">
              <MapPin className="w-2.5 h-2.5" /> {event.city || "TBA"}
            </span>
          </div>

          {/* Top-right — status chips */}
          <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5 pr-10">
            {isPast && <span className="bg-black/80 border border-white/20 text-white/80 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest rounded-full" data-testid={`sold-out-${event.id}`}>Ended</span>}
            {event.event_type === "free" && !isPast && <span className="bg-lime text-ink px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest rounded-full">Free</span>}
            {event.event_type === "invite_only" && !isPast && <span className="bg-magenta text-white px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest rounded-full">Invite</span>}
            {!isPast && event.featured && <span className="bg-magenta text-white px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest rounded-full">Featured</span>}
            {!isPast && Number(event.tickets_left) > 0 && (
              <span className="bg-orangex text-ink px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest rounded-full" data-testid={`selling-fast-${event.id}`}>{Number(event.tickets_left)} left</span>
            )}
          </div>

          {/* Bottom — poster headline with lime marker on tail word */}
          <div className="absolute inset-x-0 bottom-0 p-4">
            <div className="font-fancy uppercase text-white leading-[0.95] line-clamp-3 text-[22px] md:text-2xl">
              {titleHead && <span>{titleHead} </span>}
              <span className={accentConfig.marker}>{titleTail}</span>
            </div>
            <div className="mt-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {!isPast && Number(event.going_count) > 0 && (
                  <span className="font-mono text-[9px] uppercase tracking-widest text-lime" data-testid={`going-badge-${event.id}`}>
                    {Number(event.going_count).toLocaleString("en-IN")} going
                  </span>
                )}
                {!isPast && minPrice > 0 && (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/80">from ₹{minPrice}</span>
                )}
                {event.event_type === "free" && !isPast && minPrice === 0 && (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/80">Free RSVP</span>
                )}
              </div>
              <span className="inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-widest text-white/90 group-hover:text-lime transition-colors">
                Open <ArrowUpRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </div>
      </Link>

      {user && !isPast && (
        <button onClick={toggleFav} data-testid={`fav-btn-${event.id}`}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all ${isFav ? "bg-magenta text-white" : "bg-black/60 backdrop-blur text-white border border-white/20 hover:bg-black/80"}`}>
          <Heart className="w-3.5 h-3.5" fill={isFav ? "currentColor" : "none"} />
        </button>
      )}
    </div>
  );
}
